/*!
 * FrontAlign v1.1.0
 * (c) Eyruz Badalzada
 * Released under the MIT License
 * https://www.frontalign.dev
 */

const SELECTOR_FIXED = "[data-fixed-lock]";
const SELECTOR_STICKY = "[data-sticky-lock]";

export default class ScrollBarHelper {
  /**
   * Creates a scrollbar helper bound
   * to the document body.
   */
  constructor() {
    //SSR guard
    if (typeof window === "undefined" || typeof document === "undefined")
      return;
    this._body = document.body;
    this._html = document.documentElement;
  }

  /**
   * Returns the scrollbar's width in pixels.
   */
  getWidth() {
    return Math.abs(window.innerWidth - document.documentElement.clientWidth);
  }

  /**
   * Locks body scroll and compensates,
   * fixed/sticky elements for the scrollbar.
   */
  hide() {
    if (!this._body) return;
    const count = parseInt(this._body.dataset.faLockCount || "0", 10) + 1;
    this._body.dataset.faLockCount = String(count);

    if (this._body.dataset.faScrollLocked === "true") return;

    const width = this.getWidth();

    this._body.dataset.faScrollLocked = "true";
    this._setInline(this._html, "overflow-anchor", "none");
    this._setInline(this._body, "overflow-anchor", "none");

    document.documentElement.style.setProperty(
      "--fa-scrollbar-width",
      `${width}px`,
    );
    const fixedEls = width
      ? Array.from(document.querySelectorAll(SELECTOR_FIXED))
      : [];
    const stickyEls = width
      ? Array.from(document.querySelectorAll(SELECTOR_STICKY))
      : [];

    const bodyPadding = width
      ? parseFloat(getComputedStyle(this._body).paddingRight) || 0
      : 0;
    const fixedPaddings = fixedEls.map(
      (el) => parseFloat(getComputedStyle(el).paddingRight) || 0,
    );
    const stickyMargins = stickyEls.map(
      (el) => parseFloat(getComputedStyle(el).marginRight) || 0,
    );

    this._setInline(this._body, "overflow", "hidden");

    if (width) {
      this._writeAttr(this._body, "padding-right", `${bodyPadding + width}px`);

      fixedEls.forEach((el, i) => {
        this._writeAttr(el, "padding-right", `${fixedPaddings[i] + width}px`);
      });

      stickyEls.forEach((el, i) => {
        this._writeAttr(el, "margin-right", `${stickyMargins[i] + width}px`);
      });
    }
  }

  /**
   * Unlocks body scroll and restores,
   * the original inline styles.
   */
  reset() {
    if (!this._body) return;

    const count = Math.max(
      0,
      parseInt(this._body.dataset.faLockCount || "0", 10) - 1,
    );

    if (count > 0) {
      this._body.dataset.faLockCount = String(count);
      return;
    }

    delete this._body.dataset.faLockCount;

    if (this._body.dataset.faScrollLocked !== "true") return;

    document.documentElement.style.removeProperty("--fa-scrollbar-width");

    this._resetInline(this._body, "overflow");
    this._resetAttr(this._body, "padding-right");

    document.querySelectorAll(SELECTOR_FIXED).forEach((el) => {
      this._resetAttr(el, "padding-right");
    });

    document.querySelectorAll(SELECTOR_STICKY).forEach((el) => {
      this._resetAttr(el, "margin-right");
    });

    delete this._body.dataset.faScrollLocked;

    // Restore scroll-anchoring behaviour once layout is stable again.
    this._resetInline(this._html, "overflow-anchor");
    this._resetInline(this._body, "overflow-anchor");
  }

  /**
   * Returns whether the page currently,
   * has a visible scrollbar.
   */
  isOverflowing() {
    return this.getWidth() > 0;
  }

  /**
   * Sets an inline style, saving the original value for later reset.
   */
  _writeAttr(el, prop, value) {
    const key = this._key(prop);

    if (!(key in el.dataset)) {
      el.dataset[key] = el.style.getPropertyValue(prop);
    }

    el.style.setProperty(prop, value);
  }

  /**
   * Restores an inline style to its value saved by _writeAttr.
   */
  _resetAttr(el, prop) {
    const key = this._key(prop);

    if (!(key in el.dataset)) return;

    const saved = el.dataset[key];

    if (saved) {
      el.style.setProperty(prop, saved);
    } else {
      el.style.removeProperty(prop);
    }

    delete el.dataset[key];
  }

  /**
   * Sets an inline style, saving the original value for later reset.
   */
  _setInline(el, prop, value) {
    const key = this._key(prop);

    if (!(key in el.dataset)) {
      el.dataset[key] = el.style.getPropertyValue(prop);
    }

    el.style.setProperty(prop, value);
  }

  /**
   * Restores an inline style to its value saved by _setInline.
   */
  _resetInline(el, prop) {
    const key = this._key(prop);

    if (!(key in el.dataset)) return;

    const saved = el.dataset[key];

    if (saved) {
      el.style.setProperty(prop, saved);
    } else {
      el.style.removeProperty(prop);
    }

    delete el.dataset[key];
  }

  /**
   * Converts a CSS property to its matching dataset key.
   */
  _key(prop) {
    return "fa" + prop.replace(/-./g, (c) => c[1].toUpperCase());
  }
}
